import { useCallback, useMemo, useEffect } from "react";
import { useQuery, UseQueryOptions } from "@tanstack/react-query";
import { GroupBase } from "react-select";
import { OptionType } from "@/components/select-input/select-input";
import { SampleTagDto, Tag, TagSummaryDto } from "@/client";
import { PaginatedResponseDto } from "@/client/extend";
import { DataTableContextData } from "@/components/custom/data-table/data-table-context";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

type Option = OptionType;

// Create a non-readonly version of OptionsOrGroups
type MutableOptionsOrGroups = Array<Option>;

export type TagsList = Tag[];
type TagLike = Tag | SampleTagDto;

type UpdateFunction<F> = {
  mutateAsync: (params: {
    requestBody: {
      query: F;
      assignTags: Tag[];
      unAssignTags: Tag[];
    };
  }) => Promise<any>;
};

export type UseTagsProps<
  TRow extends { [K in keyof TRow]: TagLike[] | any },
  F,
> = {
  tagsListQuery: UseQueryOptions<
    PaginatedResponseDto<TagSummaryDto>,
    any,
    PaginatedResponseDto<TagSummaryDto>,
    any
  >;
  context?: DataTableContextData<TRow, any, any, any, any, any, any, any>;
  finalQuery?: F;
  updateFunction?: UpdateFunction<F>;
  invalidationCallbacks?: Array<(hasNewTags?: boolean) => void>;
  tagsField?: keyof TRow; // Optional field to specify which property contains the tags
};
type SelectTagOption = OptionType<string | number> & {
  __isNew__?: boolean;
  partial?: boolean;
  className?: string;
  isLocked?: boolean;
  tag?: { id: number; name: string };
};
export interface TagGroupFormValues {
  // groups: SelectGroupOption[];
  tags: SelectTagOption[];
}

export const useTags = <
  TRow extends { [K in keyof TRow]: TagLike[] | any },
  F,
>({
  tagsListQuery,
  context,
  finalQuery,
  updateFunction,
  invalidationCallbacks,
  tagsField = "tags" as keyof TRow, // Default to 'tags' field
}: UseTagsProps<TRow, F>) => {
  const queryResult = useQuery(tagsListQuery);
  const table = context?.useTable();

  const rows = table?.getSelectedRowModel().rows;

  const asTagOption = useCallback(
    (tag: Tag): SelectTagOption => ({
      value: tag.id ?? tag.name,
      label: tag.name,
      isLocked: tag?.isLocked,
    }),
    [],
  );

  const tagsList = queryResult.data?.results;

  const tagOptions: MutableOptionsOrGroups = useMemo(
    () => (tagsList ?? []).map(asTagOption),
    [tagsList, asTagOption],
  );
  const tagsMap = useMemo(
    () =>
      Object.fromEntries(
        (rows || []).map((row) => [
          row.id,
          (row.original?.[tagsField] as TagLike[])?.map((tag: Tag) =>
            asTagOption(tag),
          ) || [],
        ]),
      ),
    [rows, asTagOption, tagsField],
  );

  const tagUsageCounts = useMemo(() => {
    const counts: Record<string | number, number> = {};
    Object.values(tagsMap).forEach((rowTags) => {
      rowTags?.forEach((tag) => {
        if (tag) {
          const tagKey = String(tag.value);
          counts[tagKey] = (counts[tagKey] || 0) + 1;
        }
      });
    });
    return counts;
  }, [tagsMap]);

  const { tagGroups, tagsOptionsCurrent } = useMemo(() => {
    const tagsOptionsCurrent: SelectTagOption[] = [];

    // Total number of rows selected
    const totalRowsCount = rows?.length || 0;

    // Tags that exist on all rows (complete tags)
    const completeTags = Object.entries(tagUsageCounts)
      .filter(([_, count]) => count === totalRowsCount && totalRowsCount > 0)
      .map(([tagKey]) => {
        // Find the original tag option
        const originalOption = tagOptions.find(
          (option) =>
            (typeof option.value === "string"
              ? option.value
              : option.value.toString()) === tagKey,
        );
        if (!originalOption) return null;
        return {
          ...originalOption,
          value: `${originalOption?.value}_complete`,
          tag: {
            id: originalOption?.value as number,
            name: originalOption?.label,
          },
        } as SelectTagOption;
      })
      .filter((item): item is SelectTagOption => item !== null)
      .sort((a, b) => (a.label || "").localeCompare(b.label || ""));

    // Tags that exist partially (on some but not all rows)
    const partialTagOptions = Object.entries(tagUsageCounts)
      .filter(([_, count]) => count > 0 && count < totalRowsCount)
      .map(([tagKey, count]) => {
        // Find the original tag option
        const originalOption = tagOptions.find(
          (option) =>
            (typeof option.value === "string"
              ? option.value
              : option.value.toString()) === tagKey,
        );
        if (!originalOption) return null;
        return {
          ...originalOption,
          label: `${originalOption.label} (partial ${count}/${totalRowsCount})`,
          partial: true,
          value: `${originalOption.value}_partial`,
          className: "bg-secondary/70",
          tag: {
            id: originalOption.value as number,
            name: originalOption.label,
          },
        } as SelectTagOption;
      })
      .filter((item): item is SelectTagOption => item !== null)
      .sort((a, b) => (a.label || "").localeCompare(b.label || ""));

    // Fill tagsOptionsCurrent: complete tags first, then partial tags
    tagsOptionsCurrent.push(...completeTags, ...partialTagOptions);

    const tagGroups: GroupBase<
      | {
          value: number | string;
          label: string;
        }
      | SelectTagOption
    >[] = [];

    // Group 1: "unused" - tags that are in tagOptions but not in any tagsMap entries
    const unusedTags = [
      ...tagOptions.filter((option) => {
        const tagKey =
          typeof option.value === "string"
            ? option.value
            : option.value.toString();
        return !tagUsageCounts[tagKey];
      }),
      ...completeTags, // Add complete tags to unused
    ];

    if (unusedTags.length > 0) {
      tagGroups.push({
        label: "Unused",
        options: unusedTags as readonly {
          value: number;
          label: string;
        }[],
      });
    }

    // Group 2: "partial" - tags that exist in tagsMap but not in all entries
    // Label format: tagName (n/total)
    const partialTags = Object.entries(tagUsageCounts)
      .filter(([_, count]) => count > 0 && count < totalRowsCount)
      .map(([tagKey, count]) => {
        // Find the original tag option to get the label
        const originalOption = tagOptions.find(
          (option) =>
            (typeof option.value === "string"
              ? option.value
              : option.value.toString()) === tagKey,
        );

        if (!originalOption) return null;

        return {
          value: originalOption.value,
          label: originalOption.label,
          countedLabel: `${originalOption.label} (${count}/${totalRowsCount})`,
        };
      })
      .filter(Boolean); // as Option[]

    if (partialTags.length > 0) {
      tagGroups.push({
        label: "Current Partial (select to assign all)",
        options: partialTags as readonly {
          value: number;
          label: string;
        }[],
      });
    }

    const partialLabeledTags = partialTags.map((tag) => ({
      value: `${tag?.value}_partial`,
      label: tag?.countedLabel,
    }));
    if (partialLabeledTags.length > 0) {
      tagGroups.push({
        label: "Partial",
        options: partialLabeledTags.map(
          (tag) =>
            ({
              label: tag.label,
              value: tag.value,
              partial: true,
              className: "bg-secondary/70",
              tag: {
                id: parseInt(tag.value.toString().split("_")[0]),
                name: tag.label,
              },
            }) as SelectTagOption,
        ),
      });
    }

    return { tagGroups, tagsOptionsCurrent };
  }, [table, rows, tagOptions, tagUsageCounts]);

  // Create form schema for tag management
  const formSchema = useMemo(
    () =>
      z.object({
        tags: z.optional(
          z.array(
            z.object({
              __isNew__: z.boolean().optional(),
              label: z.string(),
              value: z.union([z.string(), z.number()]),
              partial: z.boolean().optional(),
              tag: z.object({ id: z.number(), name: z.string() }).optional(),
            }),
          ),
        ),
      }) satisfies z.ZodType<Partial<TagGroupFormValues>>,
    [],
  );

  // Create form with default values
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      tags: tagsOptionsCurrent,
    },
  });
  // Reset form when tagsOptionsCurrent changes
  useEffect(() => {
    form.reset({
      tags: tagsOptionsCurrent,
    });
  }, [tagsOptionsCurrent, form]);

  // Create the onSubmit function
  const createOnSubmit = useCallback(
    (
      updateFn: UpdateFunction<F>,
      invalidationCbs: Array<(hasNewTags?: boolean) => void>,
      query: F,
    ) => {
      return async (data: z.infer<typeof formSchema>) => {
        const assignTags: Tag[] = [];
        const unAssignTags: Tag[] = [];

        // Get the original tag IDs from tagsOptionsCurrent
        // both complete and partial
        const originalTagIds = tagsOptionsCurrent
          .map((tag) => tag.tag?.id || tag.value)
          .filter(Boolean);

        // Get the current tag IDs from the form data
        // both complete and partial
        const currentTagIds = (data.tags || [])
          .map((tag) => tag.tag?.id || tag.value)
          .filter(Boolean);

        // unAssignTags: tag names from tagsOptionsCurrent that don't exist in data.tags
        const removedTagIds = originalTagIds.filter(
          (id) => !currentTagIds.includes(id),
        ); // Find the corresponding tag for the removed tag IDs
        removedTagIds.forEach((tagId) => {
          const originalTag = tagsOptionsCurrent.find(
            (tag) => (tag.tag?.id || tag.value) === tagId,
          );
          if (originalTag?.tag) {
            unAssignTags.push(originalTag.tag);
          }
        });

        // assignTags: tags from data.tags that don't exist in tagsOptionsCurrent (newly added tags)
        const addedTagIds = currentTagIds.filter(
          (id) => !originalTagIds.includes(id),
        );

        // Find the corresponding tag for the added tag IDs
        addedTagIds.forEach((tagId) => {
          const addedTag = (data.tags || []).find(
            (tag) => (tag.tag?.id || tag.value) === tagId,
          );
          if (addedTag?.tag) {
            assignTags.push(addedTag.tag);
          } else if (addedTag) {
            assignTags.push({
              id: typeof addedTag.value === "number" ? addedTag.value : -1,
              name: addedTag.label,
            });
          }
        });

        const partialTagOptions = tagsOptionsCurrent.filter(
          (tag) => tag.partial && tag.partial === true,
        );
        const nonPartialTagOptions =
          data.tags?.filter((tag) => !tag.partial && !tag.__isNew__) || [];

        nonPartialTagOptions.forEach((tag) => {
          const tagId = tag.tag?.id || tag.value;
          const elevatedTag = partialTagOptions.find(
            (partialTag) => tagId === partialTag.tag?.id,
          );
          if (elevatedTag) {
            assignTags.push({
              id: tag.tag?.id || (tag.value as number),
              name: tag.label,
            });
          }
        });

        await updateFn.mutateAsync({
          requestBody: {
            query,
            assignTags,
            unAssignTags,
          },
        });

        // Only invalidate queries if the mutation was successful
        const hasNewTags = assignTags.some((tag: Tag) => tag.id === -1);
        invalidationCbs.forEach((callback) => callback(hasNewTags));
      };
    },
    [tagsOptionsCurrent],
  );

  // Create the onSubmit function if all required parameters are provided
  const onSubmit = useMemo(() => {
    if (updateFunction && invalidationCallbacks && finalQuery) {
      return createOnSubmit(updateFunction, invalidationCallbacks, finalQuery);
    }
    return undefined;
  }, [createOnSubmit, updateFunction, invalidationCallbacks, finalQuery]);

  return {
    tagOptions,
    tagGroups,
    tagsOptionsCurrent,
    formSchema,
    form,
    onSubmit,
  };
};
